
import { useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface ModuleContent {
  id: string
  title: string
  description: string | null
  summary: string | null
  content_type: string
  video_url: string | null
  infographic_url: string | null
  content_text: string | null
  duration: string | null
  order_index: number | null
  module_id: string
  // New optional metadata for uploaded videos
  video_file_path?: string | null
  video_file_name?: string | null
  video_file_size?: number | null
}

export const useModuleContent = () => {
  const [moduleContents, setModuleContents] = useState<{ [key: string]: ModuleContent[] }>({})
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  const fetchContent = async (moduleId: string) => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('learn_content')
        .select('*')
        .eq('module_id', moduleId)
        .order('order_index')

      if (error) throw error

      setModuleContents(prev => ({
        ...prev,
        [moduleId]: data || []
      }))
    } catch (error: any) {
      console.error('Error fetching module content:', error)
      toast({
        title: "Error",
        description: "Failed to load module content",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const createContent = async (contentData: Omit<ModuleContent, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('learn_content')
        .insert({
          title: contentData.title,
          description: contentData.description,
          summary: contentData.summary,
          content_type: contentData.content_type,
          video_url: contentData.video_url,
          infographic_url: contentData.infographic_url,
          content_text: contentData.content_text,
          duration: contentData.duration,
          order_index: contentData.order_index,
          module_id: contentData.module_id,
          // Persist uploaded video metadata when present
          video_file_path: contentData.video_file_path ?? null,
          video_file_name: contentData.video_file_name ?? null,
          video_file_size: contentData.video_file_size ?? null
        })
        .select()

      if (error) throw error
      console.log('Content created successfully:', data)
      return data
    } catch (error: any) {
      console.error('Error creating content:', error)
      throw error
    }
  }

  const updateContent = async (contentId: string, contentData: Partial<ModuleContent>) => {
    try {
      const updateData = {
        title: contentData.title,
        description: contentData.description,
        summary: contentData.summary,
        content_type: contentData.content_type,
        video_url: contentData.video_url,
        infographic_url: contentData.infographic_url,
        content_text: contentData.content_text,
        duration: contentData.duration,
        order_index: contentData.order_index,
        updated_at: new Date().toISOString(),
        // Update uploaded video metadata when present
        video_file_path: contentData.video_file_path ?? null,
        video_file_name: contentData.video_file_name ?? null,
        video_file_size: contentData.video_file_size ?? null
      }

      const { data, error } = await supabase
        .from('learn_content')
        .update(updateData)
        .eq('id', contentId)
        .select()

      if (error) throw error

      if (!data || data.length === 0) {
        throw new Error('No content was updated. Please check if the content ID is correct.')
      }

      console.log('Content updated successfully:', data[0])
      return data[0]
    } catch (error: any) {
      console.error('Error updating content:', error)
      throw error
    }
  }

  const deleteContent = async (contentId: string) => {
    try {
      const { error } = await supabase
        .from('learn_content')
        .delete()
        .eq('id', contentId)

      if (error) throw error
      console.log('Content deleted successfully')
    } catch (error: any) {
      console.error('Error deleting content:', error)
      throw error
    }
  }

  return {
    moduleContents,
    loading,
    fetchContent,
    createContent,
    updateContent,
    deleteContent
  }
}
